import type { BiImportFailure } from '@repo/front-end-services';

/**
 * The words the Launch step uses for a BI update that did not complete — `twin-launch-history-bi-push`
 * R3.1 / R5. One place, so the Launch banner and the success summary's "not updated" row say the
 * same thing about the same failure.
 *
 * Every sentence names what happened rather than guessing at why: the middleware classifies the
 * failure from the database's own error code, so "another BI update was running" is a fact, and the
 * old "timed out on a large dataset" — said of every failure that mentioned a timeout — is gone.
 */
export const biFailureReason = (failure: BiImportFailure): string => {
  if (failure.outcomeUnknown) {
    return 'We lost contact with BI before it answered. It may still have finished; retrying is safe.';
  }
  switch (failure.reason) {
    case 'lock_contention':
      return 'Another BI update was running.';
    case 'statement_timeout':
      return 'BI ran out of time on this update.';
    case 'in_progress':
      return 'A BI update for this twin is already running.';
    case 'not_found':
      return "BI could not find this twin's model.";
    case 'no_entities':
      return "This twin's model has nothing BI can import.";
    case 'other':
    default:
      // The middleware's own sentence ("The spatial hierarchy update failed: …."), not prefixed
      // again here; closed with a full stop so the summary's next sentence does not run into it.
      return asSentence(failure.message);
  }
};

const asSentence = (text: string): string => {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

/**
 * Which hierarchy the failure stopped, said as a sentence for the banner's body. When BI did not
 * answer, nothing can be said about whether the stage completed, so this says that rather than
 * contradicting the reason that follows.
 */
export const biFailureScope = (failure: BiImportFailure): string => {
  if (failure.outcomeUnknown) {
    // A spatial-stage unknown is already the banner's title ("BI may not have been updated").
    return failure.stage === 'manufacturer'
      ? 'The spatial hierarchy was updated; the manufacturer hierarchy may not have been.'
      : '';
  }
  return failure.stage === 'manufacturer'
    ? 'The spatial hierarchy was updated; the manufacturer hierarchy was not.'
    : 'Neither the spatial nor the manufacturer hierarchy was updated.';
};
